import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  extractFileNameFromDisposition,
  generateFallbackFileName,
  saveBlobAsFile,
} from '../utils/exportUtils';

/**
 * Exports a timesheet document for the given company, month, and year.
 * Depending on the `timesheetSubmission` flag, it either triggers a download
 * of the ZIP file (Excel + attachments), or submits the timesheet and sends it via email.
 *
 * The response can be:
 * - a downloadable ZIP file (Content-Type: application/zip)
 * - or a JSON message (Content-Type: application/json), typically when submission is triggered
 *
 * @param {Object} payload - Data required for exporting the timesheet.
 * @param {string} payload.companyId - The ID of the company.
 * @param {string} payload.month - The name of the month (e.g., "September").
 * @param {number} payload.year - The year (e.g., 2025).
 * @param {boolean} payload.timesheetSubmission - If true, timesheet will be submitted and emailed instead of downloaded.
 *
 * @returns {Promise<Object>} A promise that resolves with either:
 *   - { message: string } if a JSON response is returned
 *   - or triggers a ZIP file download and returns a success message
 *
 * @throws {Error} Will throw an error if the export request fails or times out.
 */
// Separate absence report (Excel) for non-employment workers. payload: { companyId, year }
export const exportNonEmploymentAbsence = async (payload) => {
  try {
    const response = await axiosClient.post('/export/absence', payload, {
      responseType: 'blob',
      timeout: 60000,
    });
    const contentType = response.headers['content-type'];
    if (contentType.includes('application/json')) return response;
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);
    saveBlobAsFile(response.data, contentType, fileName);
    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export absence report:', error);
    throw error;
  }
};

export const exportTimeSheet = async (payload) => {
  try {
    const response = await axiosClient.post('/export/timesheet', payload, {
      responseType: 'blob',
      // PDFs for every attachment are generated on the fly (Puppeteer,
      // concurrency 1), so large companies take well over a minute. Match the
      // admin export's generous window instead of aborting at 60s.
      timeout: 300000,
    });

    const contentType = response.headers['content-type'];

    if (contentType.includes('application/json')) return response;

    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export timesheet:', error);
    throw error;
  }
};

/**
 * Sends a POST request to download the administrative timesheet export as a ZIP file.
 *
 * This function submits the given payload to the `/export/timesheet-admin` endpoint,
 * expects a binary (blob) ZIP response, extracts the filename from the response headers,
 * and saves the file locally using the `saveBlobAsFile` utility.
 *
 * - Sets a 60-second timeout in case the export operation takes longer.
 * - Automatically falls back to a generated filename if one is not provided in headers.
 *
 * @param {Object} payload - Data used to generate the timesheet export.
 * @param {string} payload.companyId - ID of the company.
 * @param {number} payload.year - Year of the timesheet export.
 * @param {string|number} payload.month - Month of the timesheet export (e.g., 'May' or 5).
 *
 * @returns {Promise<{ message: string }>} Success message when the file is saved.
 * @throws {Error} Throws an error if the request fails or response is invalid.
 */
export const exportTimeSheetAdmin = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/export/timesheet-admin',
      payload,
      {
        responseType: 'blob',
        timeout: 180000, // 3 minutes for large exports with many attachments
      }
    );

    const contentType = response.headers['content-type'];

    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export timesheet:', error);
    throw error;
  }
};

/**
 * Exports employee data by sending a GET request with query parameters,
 * and downloads the response as a file (typically Excel).
 *
 * The response is expected to be a blob (e.g., application/vnd.openxmlformats-officedocument.spreadsheetml.sheet),
 * which is automatically saved to the user's device using the appropriate filename.
 *
 * @param {Object} params - Query parameters used for filtering and pagination.
 * @param {string} params.companyId - Required ID of the company.
 * @param {Object} [params.select={}] - Optional Mongo-style projection object for selecting fields.
 * @param {Object} [params.sorter={}] - Optional sorting options.
 * @param {number} [params.skip=0] - Number of records to skip (pagination).
 * @param {number} [params.offset=0] - Offset used in paginated exports (optional, often backend-specific).
 * @param {number} [params.limit] - Maximum number of records to export. If undefined, no limit is applied.
 * @param {Object|null} [params.filter=null] - Optional filtering criteria (will be stringified).
 *
 * @returns {Promise<Object>} A promise that resolves with a success message after the file is downloaded.
 * @throws {Error} Will throw if the request fails or file saving encounters an issue.
 */
export const exportEmployees = async (params) => {
  try {
    const {
      companyId,
      select = {},
      sorter = {},
      skip = 0,
      offset = 0,
      limit,
      filter = null,
      includeAdditionalInfo = false,
      includeNonEmployment = false,
    } = params;

    const requestParams = {
      companyId,
      select,
      sorter,
      skip: skip,
      offset: offset,
    };

    if (limit !== undefined) {
      requestParams.limit = limit;
    }

    if (filter) {
      requestParams.filter = filter;
    }

    if (includeAdditionalInfo) {
      requestParams.includeAdditionalInfo = true;
    }

    if (includeNonEmployment) {
      requestParams.includeNonEmployment = true;
    }

    const response = await axiosClient.get('/export/employees', {
      params: requestParams,
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }
        return qs.stringify(params, {
          encode: false,
          arrayFormat: 'brackets',
        });
      },
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to download document:', error);
    throw error;
  }
};

/**
 * Downloads a static Excel sample file from the backend based on the provided type.
 *
 * Sends a GET request to the /export/samples endpoint with a query parameter `type`
 * (e.g., 'employees' or 'products') and saves the blob response as a file using the
 * appropriate Content-Disposition filename or a generated fallback.
 *
 * The backend determines the content and structure of the sample file based on the `type` value.
 *
 * @param {string} [type='employees'] - The type of sample file to download. Defaults to 'employees'.
 *                                       Supported values typically include 'employees', 'products', etc.
 *
 * @returns {Promise<Object>} A promise that resolves with a success message after the file is saved locally.
 * @throws {Error} Throws if the request fails or the file cannot be saved.
 */
export const exportSamples = async (type = 'employees') => {
  try {
    const response = await axiosClient.get('/export/samples', {
      params: { type },
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Sample downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export sample:', error);
    throw error;
  }
};

/**
 * Downloads a PDF document from a given URL (e.g., S3 or backend proxy).
 *
 * Fetches the file as a Blob and saves it locally using the Content-Disposition
 * filename from response headers, or falls back to a default name.
 *
 * @param {string} pdfUrl - The direct URL to the PDF file.
 * @returns {Promise<Object>} A promise that resolves with a success message after the file is saved.
 * @throws {Error} Throws if the request fails or the file cannot be saved.
 */
export const exportPdfDocument = async (pdfUrl) => {
  try {
    const response = await axiosClient.get(pdfUrl, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];

    const fileName =
      extractFileNameFromDisposition(disposition) || 'dokument.pdf';

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'PDF downloaded successfully.' };
  } catch (error) {
    console.error('Failed to download PDF:', error);
    throw error;
  }
};

/**
 * Universal data export function - exports various data types to Excel.
 *
 * Supports the following export types:
 * - incomingInvoices: Purchase invoices
 * - outgoingInvoices: Sales invoices (eSalesInvoices + regular)
 * - archiveInvoices: Archived invoices
 * - customers: Customer records
 * - products: Product records
 *
 * @param {Object} params - Export parameters
 * @param {string} params.companyId - Required company ID
 * @param {string} params.exportType - Type of data to export
 * @param {Object} [params.filters={}] - Optional filters matching frontend table filters
 * @param {Object} [params.sorter={}] - Optional sorter configuration
 * @returns {Promise<Object>} Success message after download
 */
export const exportData = async (params) => {
  try {
    const { companyId, exportType, filters = {}, sorter = {} } = params;

    const requestParams = {
      companyId,
      exportType,
    };

    // Only add filter/sorter if they have values
    // Note: backend expects 'filter' (singular) not 'filters'
    if (Object.keys(filters).length > 0) {
      requestParams.filter = filters;
    }
    if (Object.keys(sorter).length > 0) {
      requestParams.sorter = sorter;
    }

    const response = await axiosClient.get('/export/data', {
      params: requestParams,
      paramsSerializer: (params) => {
        // Stringify filter and sorter if present
        const serialized = { ...params };
        if (serialized.filter) {
          serialized.filter = JSON.stringify(serialized.filter);
        }
        if (serialized.sorter) {
          serialized.sorter = JSON.stringify(serialized.sorter);
        }
        return qs.stringify(serialized, {
          encode: false,
          arrayFormat: 'brackets',
        });
      },
      responseType: 'blob',
      timeout: 120000, // 2 minutes for large exports
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export data:', error);
    throw error;
  }
};

/**
 * Downloads the company's purchase book in the company's own layout.
 *
 * A separate call and not a third `exportType`, because it produces a
 * different kind of thing: their 23-column handover sheet, headers and all,
 * rather than our table written to Excel.
 *
 * @param {Object} params
 * @param {string} params.companyId
 * @param {Object} [params.filters] - The same object the table was built from.
 * @param {Object} [params.sorter]
 * @param {boolean} [params.includePdfs] - Return a zip with the invoice PDFs
 *   beside the workbook. Optional on purpose: the workbook is small and
 *   immediate, the bundle is neither.
 * @returns {Promise<Object>}
 */
/**
 * Builds the auditors' table from the two AB Soft exports.
 *
 * 🔑 A POST with files, unlike its neighbours: the accounting journal is not
 * ours to read. It sits in AB Soft's `fipo` behind their VPN, so the year's
 * journal and the partner catalogue are exported there and handed over here.
 *
 * The answer is one workbook when the journal carries a single project and a
 * zip when it carries several — the server says which through `content-type`,
 * and the name comes off `content-disposition`, so neither is assumed.
 */
export const exportAuditTable = async ({
  companyId,
  journalFile,
  partnersFile,
  year = '',
  projects = [],
  onProgress,
}) => {
  try {
    const form = new FormData();

    form.append('companyId', companyId);
    form.append('journal', journalFile);

    if (partnersFile) form.append('partners', partnersFile);
    if (year) form.append('year', String(year));
    if (projects.length) form.append('projects', projects.join(','));

    const response = await axiosClient.post('/export/audit-table', form, {
      responseType: 'blob',
      // A year of the ledger is around 70.000 rows and a workbook per project;
      // the two minutes the ordinary exports allow are not enough.
      timeout: 600000,
      onDownloadProgress: onProgress
        ? (event) => onProgress(event.loaded || 0, event.total || 0)
        : undefined,
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to build the auditors table:', error);
    throw error;
  }
};

export const exportInvoiceJournal = async ({
  companyId,
  filters = {},
  sorter = {},
  includePdfs = false,
  // `sales` downloads the outgoing book from its own route; the request and the
  // response are otherwise the same shape.
  kind = 'purchase',
  // Called with the bytes received so far. 🚨 There is no total to divide by:
  // the archive is streamed as it is built, so the response carries no
  // `Content-Length` and a percentage would have to be invented. What can be
  // shown honestly is how much has arrived and that it is still arriving —
  // which is the difference between a download and a frozen button.
  onProgress,
}) => {
  try {
    const requestParams = { companyId };

    if (includePdfs) requestParams.includePdfs = 'true';
    // 🚨 `filter`, singular. The backend reads `req.query.filter`, and a
    // request sending `filters` comes back with every month in the book while
    // looking exactly like a filtered one.
    if (Object.keys(filters).length > 0) requestParams.filter = filters;
    if (Object.keys(sorter).length > 0) requestParams.sorter = sorter;

    const path =
      kind === 'sales' ? '/export/sales-invoice-journal' : '/export/invoice-journal';

    const response = await axiosClient.get(path, {
      params: requestParams,
      paramsSerializer: (params) => {
        const serialized = { ...params };

        if (serialized.filter) serialized.filter = JSON.stringify(serialized.filter);
        if (serialized.sorter) serialized.sorter = JSON.stringify(serialized.sorter);

        return qs.stringify(serialized, { encode: false, arrayFormat: 'brackets' });
      },
      responseType: 'blob',
      // The bundle reads every PDF out of storage; a month of a busy company is
      // well past the two minutes the plain export allows.
      timeout: includePdfs ? 900000 : 120000,
      onDownloadProgress: onProgress
        ? (event) => onProgress(event.loaded || 0, event.total || 0)
        : undefined,
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export the invoice journal:', error);
    throw error;
  }
};

/**
 * Exports a Halcom-compatible pain.001 XML payment order for selected purchase invoices.
 *
 * @param {Object} params
 * @param {string} params.companyId - Company ID
 * @param {string[]} params.invoiceIds - Array of purchase invoice IDs
 * @param {string} params.executionDate - Execution date (YYYY-MM-DD)
 * @param {string} [params.debtorAccount] - Optional specific debtor bank account
 * @returns {Promise<Object>} Success message after download
 */
export const exportHalcomPayment = async ({
  companyId,
  invoiceIds,
  executionDate,
  debtorAccount,
  groups,
  format = 'txt',
  confirmResend = false,
}) => {
  try {
    // `groups: [{ debtorAccount, invoiceIds }]` — one file per paying account, a ZIP when several.
    const response = await axiosClient.post(
      '/export/halcom-payment',
      { companyId, invoiceIds, executionDate, debtorAccount, groups, format, confirmResend },
      { responseType: 'blob', timeout: 60000 }
    );

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'XML downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export Halcom payment XML:', error);
    throw error;
  }
};

/**
 * For each selected invoice: the amount the order would carry and, when it
 * cannot go in, the reason (`paid`, `noAccount`, `currency`, …).
 *
 * @returns {Promise<{invoices: Array<{_id: string, amount: number, reason: string|null, message: string|null}>}>}
 */
export const checkHalcomPayment = async ({ companyId, invoiceIds, format }) => {
  const response = await axiosClient.post('/export/halcom-payment/check', { companyId, invoiceIds, format });
  return response.data;
};

/**
 * The file never reached the bank: takes the "sent" mark off these invoices.
 *
 * @returns {Promise<{cleared: string[], notSent: string[]}>}
 */
export const cancelHalcomPayment = async ({ companyId, invoiceIds }) => {
  const response = await axiosClient.post('/export/halcom-payment/cancel', { companyId, invoiceIds });
  return response.data;
};

/**
 * The company's accounts to pay from, each with the project it pays for, and
 * the default account per project (`defaults[projectId]`).
 *
 * @param {string} companyId
 * @returns {Promise<{accounts: Object[], defaults: Record<string, string>}>}
 */
export const getHalcomPaymentAccounts = async (companyId) => {
  const response = await axiosClient.get('/export/halcom-payment/accounts', { params: { companyId } });
  return response.data;
};

/**
 * Downloads a company-specific bonuses & deductions Excel template.
 *
 * @param {string} companyId - The company ObjectId.
 * @returns {Promise<Object>} Success message after download.
 */
export const exportBonusesSample = async (companyId) => {
  try {
    const response = await axiosClient.get('/export/samples/bonuses', {
      params: { company: companyId },
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Sample downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export bonuses sample:', error);
    throw error;
  }
};

export const exportTimesheetSummary = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/export/timesheet-summary',
      payload,
      {
        responseType: 'blob',
        timeout: 60000,
      }
    );

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export timesheet summary:', error);
    throw error;
  }
};

/**
 * Export timesheet overview as PDF.
 * Returns either downloads the blob (sync, cells <= 600) or returns
 * `{ status: 'queued', async: true }` info object (async, cells > 600).
 * Throws { code: 'OVERVIEW_RANGE_TOO_LARGE' } when cells > 1200.
 */
export const exportTimesheetOverviewPdf = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/export/timesheet-overview-pdf',
      payload,
      {
        responseType: 'blob',
        timeout: 120000,
        validateStatus: (s) => s === 200 || s === 202 || s === 422,
      }
    );

    if (response.status === 202) {
      try {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        return { ...parsed, async: true };
      } catch {
        return { status: 'queued', async: true };
      }
    }

    if (response.status === 422) {
      const text = await response.data.text();
      const parsed = JSON.parse(text);
      const err = new Error(parsed.message || 'Range too large for PDF');
      err.code = parsed.error;
      err.payload = parsed;
      throw err;
    }

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);
    saveBlobAsFile(response.data, contentType, fileName);
    return { message: 'PDF downloaded successfully.', async: false };
  } catch (error) {
    console.error('Failed to export timesheet overview PDF:', error);
    throw error;
  }
};

/**
 * Export timesheet overview as multi-sheet Excel (always sync).
 */
export const exportTimesheetOverviewExcel = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/export/timesheet-overview-excel',
      payload,
      { responseType: 'blob', timeout: 120000 }
    );
    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];
    const fileName =
      extractFileNameFromDisposition(disposition) ||
      generateFallbackFileName(contentType);
    saveBlobAsFile(response.data, contentType, fileName);
    return { message: 'Excel downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export timesheet overview Excel:', error);
    throw error;
  }
};

const ExportsService = {
  exportTimeSheet,
  exportTimeSheetAdmin,
  exportEmployees,
  exportSamples,
  exportPdfDocument,
  exportData,
  exportInvoiceJournal,
  exportAuditTable,
  exportHalcomPayment,
  exportBonusesSample,
  exportTimesheetSummary,
  exportTimesheetOverviewPdf,
  exportTimesheetOverviewExcel,
  exportNonEmploymentAbsence,
};

export default ExportsService;
